import React, { useState, useRef, useEffect } from 'react';
import { useRecaptcha } from '../hooks/useRecaptcha';
import courseData from '../data/courseData';
import { generateDateOptions, handleDateChange } from '../data/dateOptions';
import { generateRandom16Characters, validateEmail, checkCarrierEmail, adjustTextAreaHeight, isFreeOrCarrierDomain } from '../utils/formUtils'; // ユーティリティ関数をインポート
import '../styles/Form.css';

const TrialForm = () => {

  const fieldMappings = {
    last_name: 'last_name',
    first_name: 'first_name',
    email: 'email',
    age: '00N2x000008bShl',
    company: 'company',
    department: '00NRC000002Yyrp',
    position: '00NRC000002Z0Qb',
    recordType: 'recordType',
    course: '00N2x000008JkRH',
    date1: '00N2x000008IcoY',
    date2: '00N2x000008Icod',
    time1: '00N2x000008Icrh',
    time2: '00N2x000008Icrm',
    enquiry: '00N2x000006tyMQ',
    consent: '00N2x000008Icon',
    ipAddress: '00NBV000000OGuB',
    timeZone: '00NRC000000Glfh',
    priceType: '00NBV000000QiOP',
    sourceLink: '00N2x000008abJD',
    referenceId: '00N2x000008abJc'
  };

  const [formData, setFormData] = useState({
    last_name: '',
    first_name: '',
    email: '',
    confirmEmail: '',
    age: '',
    company: '',
    department: '',
    position: '',
    course: '',
    date1: '',
    time1: '',
    date2: '',
    time2: '',
    enquiry: '',
    consent: false,
    ipAddress: '',
    timeZone: '',
    priceType: '',
    sourceLink: '',
    referenceId: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [date1Options, setDate1Options] = useState([]);
  const [date2Options, setDate2Options] = useState([]);
  const [time1Options, setTime1Options] = useState([]);
  const [time2Options, setTime2Options] = useState([]);
  const [recaptchaToken, recaptchaReady, refreshRecaptchaToken] = useRecaptcha();
  const textAreaRef = useRef(null);
  const [msmaf_m_v, setMsmaf_m_v] = useState('');
  const [interactionTracked, setInteractionTracked] = useState(false);

  useEffect(() => {
    // IPアドレスを取得
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => {
        setFormData(prevData => ({
          ...prevData,
          ipAddress: data.ip
        }));
      });

    // タイムゾーンとsourceLink、生成した乱数
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const sourceLink = document.referrer;
    const randomId = generateRandom16Characters();

    setMsmaf_m_v(randomId);
    setFormData(prevData => ({
      ...prevData,
      timeZone: timezone,
      sourceLink: sourceLink,
      referenceId: randomId
    }));
  }, []);


  const handleInteraction = () => {
    if (!interactionTracked && !sessionStorage.getItem('trialFormInteractionTracked')) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'trialFormInteractionStart'
      });
      sessionStorage.setItem('trialFormInteractionTracked', 'true');
      setInteractionTracked(true);
    }
  };


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
 

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData((prevData) => ({ ...prevData, email }));
  };


  // フォーカスが外れたときのハンドラ（メールアドレスと確認用メールアドレスの検証用）
  const handleBlur = (e) => {
    const { name, value } = e.target;
    let updatedErrors = { ...formErrors };
  
    // メールアドレス欄のエラーチェック
    if (name === 'email') {
      if (!validateEmail(value)) {
        updatedErrors.email = '正しいメールアドレスを入力してください';
      } else {
        delete updatedErrors.email;
      }
      // メールアドレス（確認用）が既に入力されている場合、一致するかもチェック
      if (formData.confirmEmail && formData.confirmEmail !== value) {
        updatedErrors.confirmEmail = 'メールアドレスが一致しません';
      } else {
        delete updatedErrors.confirmEmail;
      }
    }
  
    // メールアドレス（確認用）のエラーチェック
    if (name === 'confirmEmail') {
      if (value !== formData.email) {
        updatedErrors.confirmEmail = 'メールアドレスが一致しません';
      } else {
        delete updatedErrors.confirmEmail;
      }
    }
  
    setFormErrors(updatedErrors);
  };
  

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    // confirmEmailの場合、直接更新してfieldMappingsを使用しない
    if (name === "confirmEmail") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        confirmEmail: value,
      }));
    } else {
      // 他のフィールドはfieldMappingsを使用
      const formField = Object.keys(fieldMappings).find(
        (key) => fieldMappings[key] === name
      );
      
      if (formField) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [formField]: type === 'checkbox' ? checked : value,
        }));
      }
    }
    handleInteraction();
  };

  
  const handleCourseChange = (e) => {
    handleInteraction();
    const selectedCourse = e.target.value;
    setFormData({
      ...formData,
      course: selectedCourse,
      date1: '',
      time1: '',
      date2: '',
      time2: '',
    });
    setDate1Options([]);
    setDate2Options([]);
    setTime1Options([]);
    setTime2Options([]);

    if (selectedCourse) {
      const { slots } = courseData[selectedCourse];
      const { dates } = generateDateOptions(slots);
      setDate1Options(dates);
      setDate2Options(dates);
    }
  };


  const handleDate1Change = (date) => {
    handleInteraction();
    setFormData({
      ...formData,
      date1: date,
      time1: '',
    });
    const slots = courseData[formData.course]?.slots || [];
    handleDateChange(date, setTime1Options, slots);
  };

  const handleDate2Change = (date) => {
    handleInteraction();
    setFormData({
      ...formData,
      date2: date,
      time2: '',
    });
    const slots = courseData[formData.course]?.slots || [];
    handleDateChange(date, setTime2Options, slots);
  };


  const handleTime1Change = (e) => {
    handleInteraction();
    setFormData({
      ...formData,
      time1: e.target.value,
    });
  };

  const handleTime2Change = (e) => {
    handleInteraction();
    setFormData({
      ...formData,
      time2: e.target.value,
    });
  };


  const validateForm = () => {
    let errors = {};
    if (!formData.company) errors.company = '法人・団体名を入力してください';
    if (!formData.phone) errors.phone = '電話番号を入力してください';
    if (!formData.last_name) errors.last_name = '姓を入力してください';
    if (!formData.first_name) errors.first_name = '名を入力してください';
    if (formData.email !== formData.confirmEmail) errors.confirmEmail = 'メールアドレスが一致しません';
    if (!formData.age) errors.age = 'ご年齢を選択してください';
    if (!formData.course) errors.course = 'カテゴリーを選択してください';
    if (!formData.consent) errors.consent = 'ご同意ボタンをチェックしてください';
    if (!formData.email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!validateEmail(formData.email)) {
      errors.email = '正しいメールアドレスを入力してください';
    } else if (isFreeOrCarrierDomain(formData.email)) {
      errors.email = 'フリードメインメールアドレスはご使用できません。所属法人により発行されたメールアドレスを入力してください。';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const adjustTextAreaHeight = () => {
    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.style.height = 'auto'; // 高さをリセットしてから再計算
      textArea.style.height = `${textArea.scrollHeight}px`; // コンテンツに合わせて高さを設定
    }
  };
 


  const handleSubmit = async (e) => {
    e.preventDefault();
    const isFormValid = validateForm();

    if (!isFormValid) {
      setShowErrorMessage(true);
      document.getElementById('corporateTrialForm').scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (!recaptchaReady || !recaptchaToken) {
      alert('reCAPTCHAの検証に失敗しました。再度お試しください。');
      refreshRecaptchaToken();
      return;
    }

    const mappedFormData = new FormData();
   

    Object.keys(formData).forEach((key) => {
      if (key === "confirmEmail") return; // confirmEmail を除外
      const mappedKey = fieldMappings[key];
      if (mappedKey) {
        const value = key === "consent" ? (formData.consent ? "1" : "") : formData[key];
        mappedFormData.append(mappedKey, value);
      }
    });

    mappedFormData.append('recaptcha_token', recaptchaToken);

    const response = await fetch('/wp-admin/admin-ajax.php?action=verify_recaptcha', {
      method: 'POST',
      body: mappedFormData,
    });

    const result = await response.json();

    if (!result.success) {
      alert(result.message || 'reCAPTCHA validation failed.');
      refreshRecaptchaToken();
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'trialFormSubmit',
      formData: {
        email: formData.email,
      },
    });

    e.target.submit();
  };

  return (
    <form
      id="corporateTrialForm"
      action="https://webto.salesforce.com/servlet/servlet.WebToLead"
      method="POST"
      className="p-form"
      onSubmit={handleSubmit}
    >
    <input type="hidden" name="oid" value="00D2x0000072gyz" />
    <input type="hidden" name="retURL" id="retURL" value={`https://www.eltschool.jp/thankyou?refid=${encodeURIComponent(msmaf_m_v)}`} />
    <input id="00N2x000008bShg" name="00N2x000008bShg" type="hidden" value="Trial Form" />
    <input id="00NBV000000QiOP" name="00NBV000000QiOP" type="hidden" value="New Category Plan(2024/09~)" />
    <input id="recordType" name="recordType" type="hidden" value="0122x000000t7XY" />
    <input type="hidden" id={fieldMappings.ipAddress} name={fieldMappings.ipAddress} value={formData.ipAddress} />
    <input type="hidden" id={fieldMappings.timeZone} name={fieldMappings.timeZone} value={formData.timeZone} />
    <input type="hidden" id={fieldMappings.sourceLink} name={fieldMappings.sourceLink} value={formData.sourceLink} />
    <input type="hidden" id={fieldMappings.referenceId} name={fieldMappings.referenceId} value={formData.referenceId} />


    <p className={showErrorMessage ? 'notfilled invalid' : 'notfilled'} style={{ display: showErrorMessage ? 'block' : 'none' }}>
      未記入項目があります
    </p>
      {/* お名前 (姓、名) */}
      <div className={`p-form__item ${formErrors.last_name || formErrors.first_name ? 'invalid' : ''}`}>
        <label className="p-form__head">ご担当者名<span className="p-form__required">必須</span></label>
        <div className="p-form__body p-form__two-columns">
          <div className="p-form__input-wrapper">
            <input
              id={fieldMappings.last_name}               
              name={fieldMappings.last_name}               
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="姓"
              className={formErrors.last_name ? 'invalid' : ''}
            />
            {formErrors.last_name && <span className="error invalid">{formErrors.last_name}</span>}
          </div>
          <div className="p-form__input-wrapper">
            <input
              id={fieldMappings.first_name}               
              name={fieldMappings.first_name}               
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="名"
              className={formErrors.first_name ? 'invalid' : ''}
            />
            {formErrors.first_name && <span className="error invalid">{formErrors.first_name}</span>}
          </div>
        </div>
      </div>
      {/* 法人・団体名 */}
      <div className={`p-form__item ${formErrors.company ? 'invalid' : ''}`}>
        <label className="p-form__head">法人・団体名<span className="p-form__required">必須</span></label>
        <div className="p-form__body">
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            placeholder="株式会社ELT英会話"
          />
          {formErrors.company && <span className="error invalid">{formErrors.company}</span>}
        </div>
      </div>
      {/* 部署名 */}
      <div className="p-form__item">
        <label className="p-form__head">部署名</label>
        <div className="p-form__body">
          <input
            type="text"
            id={fieldMappings.department}
            name={fieldMappings.department}
            value={formData.department}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {/* 役職名 */}
      <div className="p-form__item">
        <label className="p-form__head">役職名</label>
        <div className="p-form__body">
          <input
            type="text"
            id={fieldMappings.position}
            name={fieldMappings.position}
            value={formData.position}
            onChange={handleInputChange}
          />
        </div>
      </div>
      {/* メールアドレス */}
      <div className={`p-form__item ${formErrors.email ? 'invalid' : ''}`}>
        <label className="p-form__head">メールアドレス<span className="p-form__required">必須</span></label>
        <div className="p-form__body">
          <input
            type="email"
            id={fieldMappings.email}               
            name={fieldMappings.email}               
            value={formData.email}
            onChange={handleEmailChange} 
            onBlur={handleBlur}
            placeholder="example@email.com"
          />
        </div>
      </div>
      <div className={`p-form__item ${formErrors.confirmEmail ? 'invalid' : ''}`}>
        <label className="p-form__head">メールアドレス (確認用)<span className="p-form__required">必須</span></label>
        <div className="p-form__body">
          <input
            type="email"
            id="confirmEmail"
            name="confirmEmail"
            value={formData.confirmEmail}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="example@email.com"
          />
          {formErrors.confirmEmail && <span className="error invalid">{formErrors.confirmEmail}</span>}
        </div>
      </div>

      {/* ご年齢 */}
      <div className={`p-form__item ${formErrors.age ? 'invalid' : ''}`}>
        <label className="p-form__head">ご年齢 (受講者)<span className="p-form__required">必須</span></label>
        <div className="p-form__body">
          <select
            id={fieldMappings.age}               
            name={fieldMappings.age}               
            value={formData.age}
            onChange={handleInputChange}
          >
            <option value="">未選択 ▼</option>
            <option value="19~24">19~24歳</option>
            <option value="25~29">25~29歳</option>
            <option value="30~49">30~49歳</option>
            <option value="50~69">50~69歳</option>
            <option value="70~">70歳~</option>
          </select>
          {formErrors.age && <span className="error invalid">{formErrors.age}</span>}
        </div>
      </div>

      {/* カテゴリー */}
      <div className={`p-form__item ${formErrors.course ? 'invalid' : ''}`}>
        <label className="p-form__head">カテゴリー<span className="p-form__required">必須</span></label>
        <div className="p-form__body">
          <select
            id={fieldMappings.course}
            name={fieldMappings.course}
            value={formData.course}
            onChange={handleCourseChange}
          >
            <option value="">未選択 ▼</option>
            {Object.values(courseData).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {formErrors.course && <span className="error invalid">{formErrors.course}</span>}
        </div>
      </div>


      {/* ご希望日時 - Display only if course is selected */}
      {formData.course && (

        <div className="p-form__item">
          <label className="p-form__head">ご希望日時</label>
          <div className="p-form__body">
            <p className="p-form__subtext">
              表示される候補日以外でも空きがある場合がございます。お気軽にお申し込みください。
            </p>

            {/* 第1希望 */}
            <div className="p-form__two-columns p-form__margin-bottom">
              <div className="p-form__input-wrapper">
                <select
                  id={fieldMappings.date1}
                  name={fieldMappings.date1}
                  value={formData.date1}
                  onChange={(e) => handleDate1Change(e.target.value)}
                >
                  <option value="">第1希望日 ▼</option>
                  {date1Options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="p-form__input-wrapper">
                <select
                  id={fieldMappings.time1}
                  name={fieldMappings.time1}
                  value={formData.time1}
                  onChange={handleTime1Change}
                >
                  <option value="">時間 ▼</option>
                  {time1Options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {/* 第2希望 */}
            <div className="p-form__two-columns">
              <div className="p-form__input-wrapper">
                <select
                  id={fieldMappings.date2}
                  name={fieldMappings.date2}
                  value={formData.date2}
                  onChange={(e) => handleDate2Change(e.target.value)}
                >
                  <option value="">第2希望日 ▼</option>
                  {date2Options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="p-form__input-wrapper">
                <select
                  id={fieldMappings.time2}
                  name={fieldMappings.time2}
                  value={formData.time2}
                  onChange={handleTime2Change}
                >
                  <option value="">時間 ▼</option>
                  {time2Options.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Textarea field */}
      <div className="p-form__item">
        <label className="p-form__head">ご要望・お問い合わせ</label>
        <div className="p-form__body">
          <div className="p-form__form-area p-form__form-area--full">
            <textarea
              id={fieldMappings.enquiry}
              name={fieldMappings.enquiry}
              wrap="soft"
              ref={textAreaRef}
              onInput={adjustTextAreaHeight} // 入力イベントで高さ調整
              style={{ overflow: 'hidden', resize: 'none' }} // スクロールを非表示、手動リサイズ禁止
            ></textarea>
          </div>
        </div>
      </div>

      {/* プライバシーポリシー */}
      <div className={`p-form__privacy ${formErrors.consent ? 'invalid' : ''}`}>
        <input
          type="checkbox"
          name={fieldMappings.consent} 
          id={fieldMappings.consent}
          value="1"
          checked={formData.consent} // checkedプロパティのみ使用
          onChange={(e) => setFormData((prevFormData) => ({
              ...prevFormData,
              consent: e.target.checked, // checkedの状態を更新
            }))
          }
          className="p-form__privacy-check"
        />
        <label htmlFor={fieldMappings.consent} className="p-form__privacy-label">
          個人情報の取扱について同意する
        </label>
      </div>
      <div className="p-form__privacy-link">
        {/* ラベルの下にリンクを表示 */}
        <a href="https://www.eltschool.jp/policy/" target="_blank" rel="noopener" >
          「個人情報の取扱について」
        </a>
      </div>
      <div className="p-form__privacy-error">
        {/* エラーメッセージをラベルの下に表示 */}
        {formErrors.consent && <span className="error invalid">{formErrors.consent}</span>}
      </div>

      {showErrorMessage && (
        <p className="error">未記入項目があります</p>
      )}

      <input type="submit" className="p-form__send" value="送信する" />
    </form>
  );
};

export default TrialForm;

